import clsx from 'clsx'
import React, { FC, forwardRef, MouseEventHandler, useCallback } from 'react'
import styled from 'styled-components'

import SvgCheck from '@Assets/SVG/Check'
import { FormElementProps } from '@Components/FormElements/FormElementProps'
import { useFormElementState } from '@Components/FormElements/useFormElementState'
import { up } from '@Globals/Style/breakpoints'
import { errorMessage } from '@Globals/Style/mixins'

export type CheckboxProps = Omit<JSX.IntrinsicElements['input'], 'type' | 'size' | 'id'>
& Pick<FormElementProps, 'error' | 'wrapperClassName'>
& {
  id: string
  indicatorClassName?: string
  label: string
  rounded?: boolean
  secondaryLabel?: string
}

export const CheckboxWrapperLabel = styled.label`
  display: flex;
  cursor: pointer;
  min-width: 0;
  width: 100%;
  padding-top: 5px;
  margin-bottom: 5px;
  position: relative;

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    opacity: 0;
    appearance: none;

    // We'll move the field to sorta kinda centered on the indicator. This will make sense if the
    // "required" attribute is used, and the browser renders a native "you need to fill this"-message.
    left: 13px;
    top: 13px;
  }
`

const StyledIndicator = styled.span`
  display: inline-block;
  position: relative;
  content: "";
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  border: 1px solid ${({ theme }) => theme.colors.greyLight};
  border-radius: 6px;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.greyDark};

  &.rounded {
    border-radius: 50%;
  }

  ${up('tablet')} {
    width: 22px;
    height: 22px;
    flex-basis: 22px;
  }

  svg {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
    width: 60%;
    height: 60%;

    &, path {
      color: inherit;
      fill: currentColor;
    }
  }

  input:checked ~ & {
    svg {
      opacity: 1;
    }
  }

  label:hover & {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  input:active ~ &,
  input:focus ~ & {
    border-width: 2px;
  }
`

const StyledTextLabel = styled.span`
  display: flex;
  flex: 1 1 auto;
  flex-wrap: wrap;
  user-select: none;
  padding: 3px 0 3px 8px;
  font-size: .875rem;
  min-width: 0;

  input:checked ~ & {
    color: ${({ theme }) => theme.colors.greyDark};
    font-weight: bold;
  }

  > span {
    display: block;
    white-space: nowrap;

    &.label {
      overflow: hidden;
      text-overflow: ellipsis;
      color: inherit;
      font-weight: inherit;
    }

    &.counter {
      flex: 0 1 auto;
      padding-left: 1em;
      color: ${({ theme }) => theme.colors.grey};
    }
  }
`

const StyledErrorMessage = styled.span`
  ${errorMessage};
  margin: .2em 0;
  flex: 0 0 100% !important;
`

export const Checkbox: FC<CheckboxProps> = forwardRef(
  (
    {
      'aria-label': ariaLabel,
      children,
      error,
      id,
      indicatorClassName = '',
      label,
      rounded,
      secondaryLabel,
      title,
      wrapperClassName = '',
      ...props
    }: CheckboxProps,
    ref,
  ) => {
    const { formActive, ...state } = useFormElementState<'input'>({ ...props })
    const { className } = props

    // A default handler to blur the checkbox after clicking it. It's purely for 𝕒𝕖𝕤𝕥𝕙𝕖𝕥𝕚𝕔 reasons.
    const blurActiveElement: MouseEventHandler<HTMLInputElement> = useCallback(({ currentTarget }) => currentTarget.blur(), [])

    return (
      <CheckboxWrapperLabel
        aria-label={ariaLabel || label}
        className={clsx({ rounded, [wrapperClassName]: Boolean(wrapperClassName) })}
        htmlFor={id}
        title={title}
      >
        <input
          aria-errormessage={error}
          aria-invalid={Boolean(error) || undefined}
          autoComplete="off"
          id={id}
          onClick={blurActiveElement}
          ref={ref}
          type="checkbox"
          {...props}
          {...state}
        />
        <StyledIndicator
          aria-hidden
          className={clsx({ [indicatorClassName]: Boolean(indicatorClassName), rounded })}
        >
          <SvgCheck />
        </StyledIndicator>
        <StyledTextLabel className={className}>
          <span className="label">{children || label}</span>
          {secondaryLabel && <span className="counter">{secondaryLabel}</span>}
          {error && <StyledErrorMessage aria-hidden>{error}</StyledErrorMessage>}
        </StyledTextLabel>
      </CheckboxWrapperLabel>
    )
  },
)
